import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  CircularProgress,
  Alert,
  Button,
  Card,
  CardContent,
  Divider,
  Link,
  Stack,
  Modal,
  IconButton,
  Snackbar
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import CloseIcon from '@mui/icons-material/Close';
import EmailIcon from '@mui/icons-material/Email';
import PolicyIcon from '@mui/icons-material/Policy';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import PhoneIcon from '@mui/icons-material/Phone';
import { formatCurrency } from '../utils/pricing';
import { getUserOrders } from '../api/orders';
import ProtectedRoute from '../components/ProtectedRoute';
import { useAuth } from '../context/AuthContext';
import ReorderButton from '../components/ReorderButton';

const OrdersPage: React.FC = () => {
  return (
    <ProtectedRoute>
      <UserOrders />
    </ProtectedRoute>
  );
};

const UserOrders: React.FC = () => {
  const [orders, setOrders] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [policyModalOpen, setPolicyModalOpen] = useState(false);
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [copySnackbarOpen, setCopySnackbarOpen] = useState(false);
  const { user } = useAuth();

  const handleCopyEmail = async () => {
    try {
      await navigator.clipboard.writeText('info@naturalsourcesnacks.ca');
      setCopySnackbarOpen(true);
    } catch (err) {
      console.error('Failed to copy email:', err);
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = 'info@naturalsourcesnacks.ca';
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopySnackbarOpen(true);
    }
  };

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await getUserOrders(rowsPerPage, page * rowsPerPage);
        
        // Filter orders to only show those belonging to the current user
        const userOrders = (response.items || []).filter((order: any) => {
          return order.email?.toLowerCase() === user?.email?.toLowerCase() ||
                 order.customerId === user?.ecwid_customer_id;
        });
        
        setOrders(userOrders);
      } catch (err: any) {
        setError(err.response?.data?.detail || 'Failed to fetch orders');
        console.error('Error fetching orders:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchOrders();
  }, [page, rowsPerPage, user]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  const getFulfillmentStatusChip = (status: string) => {
    switch (status) {
      case 'DELIVERED':
        return <Chip label="Delivered" color="success" size="small" />;
      case 'SHIPPED':
        return <Chip label="Shipped" color="primary" size="small" />;
      case 'PROCESSING':
        return <Chip label="Processing" color="warning" size="small" />;
      case 'AWAITING_PROCESSING':
        return <Chip label="Awaiting Processing" color="default" size="small" />;
      default:
        return <Chip label={status} size="small" />;
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 6 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          My Orders
        </Typography>
        <Typography variant="body1" color="text.secondary">
          View your order history and check order status
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Paper sx={{ width: '100%', mb: 4 }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Order #</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Items</TableCell>
                <TableCell>Total</TableCell>
                <TableCell>Fulfillment Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : orders.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                    <Typography variant="body1" color="text.secondary">
                      You don't have any orders yet.
                    </Typography>
                    <Button 
                      component={RouterLink} 
                      to="/" 
                      variant="contained" 
                      color="primary"
                      sx={{ mt: 2 }}
                    >
                      Start Shopping
                    </Button>
                  </TableCell>
                </TableRow>
              ) : (
                orders.map((order) => (
                  <TableRow key={order.id} hover>
                    <TableCell>#{order.orderNumber}</TableCell>
                    <TableCell>{formatDate(order.createDate)}</TableCell>
                    <TableCell>
                      {order.items.length} item{order.items.length !== 1 ? 's' : ''}
                    </TableCell>
                    <TableCell>{formatCurrency(order.total)}</TableCell>
                    <TableCell>{getFulfillmentStatusChip(order.fulfillmentStatus)}</TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1}>
                        <Button 
                          variant="outlined" 
                          size="small"
                          component={RouterLink}
                          to={`/orders/${order.id}`}
                        >
                          View Details
                        </Button>
                        <ReorderButton
                          order={order}
                          size="small"
                        />
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        
        {!loading && orders.length > 0 && (
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={-1} // We don't know the total count from the API
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        )}
      </Paper>

      <Box sx={{ mt: 4 }}>
        <Typography variant="h5" gutterBottom>
          Need Help With Your Order?
        </Typography>
        <Card>
          <CardContent>
            <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' } }}>
              <Box>
                <Typography variant="h6">Contact Customer Support</Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  Our customer service team is here to help with any questions or issues regarding your orders.
                </Typography>
                <Button 
                  variant="contained" 
                  color="primary"
                  startIcon={<EmailIcon />}
                  onClick={() => setContactModalOpen(true)}
                  sx={{ textTransform: 'none' }}
                >
                  Contact Support
                </Button>
              </Box>
              <Box>
                <Typography variant="h6">Shipping & Returns Policy</Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  Learn about our shipping methods, delivery times, and return policy.
                </Typography>
                <Button 
                  variant="outlined"
                  startIcon={<PolicyIcon />}
                  onClick={() => setPolicyModalOpen(true)}
                  sx={{ textTransform: 'none' }}
                >
                  View Policies
                </Button>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Box>

      {/* Contact Support Modal */}
      <Modal
        open={contactModalOpen}
        onClose={() => setContactModalOpen(false)}
        aria-labelledby="contact-modal-title"
        aria-describedby="contact-modal-description"
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: { xs: '90%', sm: '400px' },
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            p: 0,
            overflow: 'hidden'
          }}
        >
          {/* Modal Header */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              p: 3,
              borderBottom: '1px solid',
              borderColor: 'divider',
              bgcolor: 'primary.main',
              color: 'white'
            }}
          >
            <Typography id="contact-modal-title" variant="h5" component="h2" fontWeight="bold">
              Contact Support
            </Typography>
            <IconButton
              onClick={() => setContactModalOpen(false)}
              sx={{ color: 'white' }}
              aria-label="close"
            >
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Modal Content */}
          <Box sx={{ p: 3 }}>
            <Stack spacing={3}>
              {/* Email Section */}
              <Box>
                <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold' }}>
                  Email Us
                </Typography>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    bgcolor: 'grey.50',
                    cursor: 'pointer',
                    '&:hover': {
                      bgcolor: 'grey.100'
                    }
                  }}
                  onClick={handleCopyEmail}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <EmailIcon color="primary" />
                    <Typography variant="body1" fontWeight="medium">
                      info@naturalsourcesnacks.ca
                    </Typography>
                  </Box>
                  <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleCopyEmail(); }}>
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                </Paper>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  Click to copy email address
                </Typography>
              </Box>

              <Divider />

              {/* Phone Section */}
              <Box>
                <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold' }}>
                  Call Us
                </Typography>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    bgcolor: 'grey.50'
                  }}
                >
                  <PhoneIcon color="primary" />
                  <Link href="tel:604.243.8700" sx={{ fontWeight: 'medium', textDecoration: 'none' }}>
                    604.243.8700
                  </Link>
                </Paper>
              </Box>

              {/* Action Buttons */}
              <Box sx={{ display: 'flex', gap: 2, pt: 2 }}>
                <Button
                  variant="contained"
                  fullWidth
                  startIcon={<EmailIcon />}
                  href="mailto:info@naturalsourcesnacks.ca"
                  sx={{ textTransform: 'none' }}
                >
                  Open Email App
                </Button>
              </Box>
            </Stack>
          </Box>
        </Box>
      </Modal>

      {/* Shipping & Returns Policy Modal */}
      <Modal
        open={policyModalOpen}
        onClose={() => setPolicyModalOpen(false)}
        aria-labelledby="policy-modal-title"
        aria-describedby="policy-modal-description"
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: { xs: '90%', sm: '80%', md: '600px' },
            maxHeight: '80vh',
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            p: 0,
            overflow: 'hidden'
          }}
        >
          {/* Modal Header */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              p: 3,
              borderBottom: '1px solid',
              borderColor: 'divider',
              bgcolor: 'primary.main',
              color: 'white'
            }}
          >
            <Typography id="policy-modal-title" variant="h5" component="h2" fontWeight="bold">
              Shipping & Returns Policy
            </Typography>
            <IconButton
              onClick={() => setPolicyModalOpen(false)}
              sx={{ color: 'white' }}
              aria-label="close"
            >
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Modal Content */}
          <Box
            sx={{
              p: 3,
              maxHeight: 'calc(80vh - 80px)',
              overflowY: 'auto'
            }}
          >
            <Stack spacing={3}>
              {/* Delivery Charge Section */}
              <Box>
                <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold' }}>
                  Is there a delivery charge?
                </Typography>
                <Typography variant="body1" paragraph>
                  We offer free delivery to all of our clients with a minimum order of $99.
                </Typography>
                <Typography variant="body1" paragraph>
                  Orders under $99 have a delivery fee of $9.99
                </Typography>
              </Box>

              <Divider />

              {/* Delivery Timing Section */}
              <Box>
                <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold' }}>
                  When does my delivery arrive?
                </Typography>
                <Typography variant="body1" paragraph>
                  Delivery is dependent on the service, but all deliveries occur during business hours: Monday-Friday 8am-4pm
                </Typography>
                <Typography variant="body1" paragraph>
                  Orders placed before 10am Friday will be delivered the following Mon/Tues and orders placed before 10am Tuesday will be delivered Weds/Thurs unless otherwise specified in order comments by you.
                </Typography>
              </Box>

              <Divider />

              {/* Missing/Damaged Items Section */}
              <Box>
                <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold' }}>
                  What if something from my order is missing/damaged?
                </Typography>
                <Typography variant="body1" paragraph>
                  We take pride in making sure everything about your order is just right, so if it's not, we'll do everything to fix it promptly.
                </Typography>
                <Typography variant="body1" paragraph>
                  Either by delivering missing product or crediting your account (as per your instructions).
                </Typography>
              </Box>

              <Divider />

              {/* Order Changes Section */}
              <Box>
                <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold' }}>
                  What if I need to change something about my order?
                </Typography>
                <Typography variant="body1" paragraph>
                  No sweat! Just shoot an email to{' '}
                  <Link href="mailto:info@naturalsourcesnacks.ca" sx={{ fontWeight: 'bold' }}>
                    info@naturalsourcesnacks.ca
                  </Link>{' '}
                  or call us at{' '}
                  <Link href="tel:604.243.8700" sx={{ fontWeight: 'bold' }}>
                    604.243.8700
                  </Link>{' '}
                  and we'll make sure it's taken care of.
                </Typography>
              </Box>
            </Stack>
          </Box>
        </Box>
      </Modal>

      {/* Copy Success Snackbar */}
      <Snackbar
        open={copySnackbarOpen}
        autoHideDuration={2000}
        onClose={() => setCopySnackbarOpen(false)}
        message="Email address copied to clipboard!"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Container>
  );
};

export default OrdersPage; 